/**
 * Web push, behind an async gate.
 *
 * Everything here is lazy on purpose. `firebase/messaging` is dynamically
 * imported and `isSupported()` is awaited before any call, because push is a
 * progressive capability: iOS Safari outside an installed PWA, private
 * windows, and older browsers all lack it, and the app must not pay for — or
 * crash on — a capability the session cannot use. Nothing in this module runs
 * at import time (the auth.ts module-load hazard applies here too: this file
 * is reachable from shell-adjacent code).
 */

import { app } from "./app";

/** Absent until the operator generates the Web Push certificate in the
 *  Firebase console and fills the env var; push UI hides itself without it. */
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export function pushConfigured(): boolean {
  return Boolean(vapidKey) && "serviceWorker" in navigator;
}

async function messagingIfSupported() {
  if (!pushConfigured()) return null;
  const { getMessaging, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) return null;
  return getMessaging(app);
}

/** The service worker registration FCM will deliver through. The config
 *  travels in the registration URL — see public/firebase-messaging-sw.js. */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const config = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  });
  return navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?${config.toString()}`,
  );
}

/**
 * Ask for permission (must be called from a user gesture) and mint this
 * device's FCM token. Returns null wherever push cannot work — not supported,
 * not configured, permission denied — so callers need one branch, not four.
 */
export async function requestPushToken(): Promise<string | null> {
  const messaging = await messagingIfSupported();
  if (messaging === null) return null;
  if ((await Notification.requestPermission()) !== "granted") return null;
  const { getToken } = await import("firebase/messaging");
  return getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: await registerServiceWorker(),
  });
}

/**
 * The device's current token if permission is already granted, else null.
 * Never prompts — safe to call on page load to refresh lastSeenAt.
 */
export async function currentPushToken(): Promise<string | null> {
  if (typeof Notification === "undefined") return null;
  if (Notification.permission !== "granted") return null;
  const messaging = await messagingIfSupported();
  if (messaging === null) return null;
  const { getToken } = await import("firebase/messaging");
  return getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: await registerServiceWorker(),
  });
}

/**
 * Surrender this browser's registration token, so FCM stops delivering to it
 * and the next `getToken` mints a different string.
 *
 * Called on sign-out. A registration token belongs to the BROWSER, not the
 * account: without this, signing out as A and in as B on the same browser
 * leaves A's token document pointing at what is now B's device, and every
 * later notification for A pushes into B's browser. Nothing detects that —
 * the token stays perfectly valid, it has just changed hands.
 *
 * Resolves quietly wherever push is unavailable; a failure here must never
 * be the reason a sign-out does not happen.
 */
export async function deletePushToken(): Promise<void> {
  const messaging = await messagingIfSupported();
  if (messaging === null) return;
  const { deleteToken } = await import("firebase/messaging");
  await deleteToken(messaging);
}

/** Foreground messages (page visible): FCM suppresses the system notification,
 *  so the app surfaces them itself. Returns an unsubscribe, or a no-op. */
export async function onForegroundMessage(
  handler: (payload: { title?: string; body?: string }) => void,
): Promise<() => void> {
  const messaging = await messagingIfSupported();
  if (messaging === null) return () => undefined;
  const { onMessage } = await import("firebase/messaging");
  return onMessage(messaging, (payload) =>
    handler({
      title: payload.notification?.title,
      body: payload.notification?.body,
    }),
  );
}

/** This browser's notification permission, or null where the Notification
 *  API does not exist (iOS Safari outside an installed PWA, older browsers). */
export function notificationPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

/**
 * Call `onChange` whenever the notification permission may have changed.
 *
 * The permission changes outside the page: a person who blocked
 * notifications turns them back on in the browser's site settings, and the
 * page has to notice to offer the opt-in again. The Permissions API reports
 * that where it can. Safari and some embedded browsers either reject the
 * query for "notifications" or never fire `change`, so returning to the tab
 * triggers a re-read as well. `onChange` may therefore fire without a
 * change; callers re-read `notificationPermission()` and compare.
 */
export function watchNotificationPermission(onChange: () => void): () => void {
  let stopped = false;
  let status: PermissionStatus | null = null;
  const handler = () => onChange();

  void navigator.permissions
    ?.query({ name: "notifications" })
    .then((result) => {
      if (stopped) return;
      status = result;
      status.addEventListener("change", handler);
    })
    .catch(() => undefined);
  document.addEventListener("visibilitychange", handler);
  window.addEventListener("focus", handler);

  return () => {
    stopped = true;
    status?.removeEventListener("change", handler);
    document.removeEventListener("visibilitychange", handler);
    window.removeEventListener("focus", handler);
  };
}
